/**
 * Block 13 PR-G Phase 2 Step 2 — Kazakh (kk) amount-in-words.
 *
 * Output convention: words only (no currency word appended). Kazakh has no
 * grammatical gender and no plural form changes for cardinal numbers, so the
 * algorithm is simpler than Russian. Examples:
 *   1 → "бір"
 *   1000 → "бір мың" (explicit "бір", consistent with millions/billions)
 *   1_234_567 → "бір миллион екі жүз отыз төрт мың бес жүз алпыс жеті"
 *
 * "теңге" indeclinable — the same form regardless of count. Currency word is
 * appended by Step 3 `contractTextRender.ts`, not here.
 *
 * Conventions chosen (after research, see ADR-0025 §Q5):
 *   - 100 → "жүз" (no leading "бір" for 100; standard usage)
 *   - 1000 → "бір мың" (explicit "бір"; modern Kazakh financial usage matches
 *     the "бір миллион" / "бір миллиард" pattern)
 *
 * Reference:
 *   - ADR-0025 §Q5
 *   - Block_13_PR_G_phase_0_planning.md Phase 2 Step 2
 */

import type { AmountCurrency } from './ru';

const UNITS = ['', 'бір', 'екі', 'үш', 'төрт', 'бес', 'алты', 'жеті', 'сегіз', 'тоғыз'] as const;

/** Direct named tens — Kazakh tens are not formed by "ten + X" multiplication. */
const TENS = [
  '',
  'он',
  'жиырма',
  'отыз',
  'қырық',
  'елу',
  'алпыс',
  'жетпіс',
  'сексен',
  'тоқсан',
] as const;

/**
 * Convert 0-999 to Kazakh words. No gender / no plural — single canonical form.
 *
 * Conventions:
 *   - 100 → "жүз" (no leading "бір")
 *   - 200..900 → "{N} жүз" (e.g., "екі жүз")
 *   - 10..19 → "он [N]" (e.g., "он бір" for 11)
 *   - 21..99 (non-tens) → "{tens} {units}" (e.g., "жиырма бір")
 */
function convertGroup(n: number): string[] {
  if (n === 0) return [];
  const result: string[] = [];
  const h = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const u = n % 10;
  // Indices гарантированно в диапазоне (h/t/u: 0-9). Non-null assertions safe
  // под `noUncheckedIndexedAccess` strict mode.
  if (h === 1) {
    result.push('жүз');
  } else if (h > 1) {
    result.push(UNITS[h]!, 'жүз');
  }
  if (t > 0) result.push(TENS[t]!);
  if (u > 0) result.push(UNITS[u]!);
  return result;
}

export function amountInWordsKk(n: number, _currency: AmountCurrency): string {
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    throw new Error(`amountInWordsKk: expected finite integer, got ${n}`);
  }
  if (n < 0) {
    return `минус ${amountInWordsKk(-n, _currency)}`;
  }
  if (n === 0) {
    return 'нөл';
  }

  const billions = Math.floor(n / 1_000_000_000);
  const millions = Math.floor((n % 1_000_000_000) / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const units = n % 1000;

  const parts: string[] = [];

  if (billions > 0) {
    parts.push(...convertGroup(billions), 'миллиард');
  }
  if (millions > 0) {
    parts.push(...convertGroup(millions), 'миллион');
  }
  if (thousands > 0) {
    parts.push(...convertGroup(thousands), 'мың');
  }
  if (units > 0) {
    parts.push(...convertGroup(units));
  }

  return parts.join(' ');
}
